/**
 * Async read-write lock for coordinating shutdown safety.
 *
 * - **Read lock** = "I'm using the resource" (multiple concurrent readers allowed)
 * - **Write lock** = "I'm shutting down" (exclusive, waits for all readers to finish)
 *
 * The write lock is acquired once (during shutdown) and **never released** —
 * all subsequent read-lock attempts fail immediately.
 *
 * @example
 * class DatabaseWrapper {
 *   private rwLock = new ReadWriteLock();
 *
 *   async getRecord(id: string): Promise<any> {
 *     if (!this.rwLock.acquireRead()) return null; // shutting down
 *     try {
 *       return await this.db.get(id);
 *     } finally {
 *       this.rwLock.releaseRead();
 *     }
 *   }
 *
 *   async shutdown(): Promise<void> {
 *     await this.rwLock.acquireWrite();
 *     await this.db.close();
 *   }
 * }
 */
export class ReadWriteLock {
  private readers = 0;
  private shuttingDown = false;
  // Every waiting acquireWrite gets its own entry. This was ONE field, which meant a
  // second overlapping acquireWrite overwrote the first one's resolve and stranded it
  // for ever — in DatabaseWrapper.shutdownAsync that left the PouchDB handles open and
  // the shutdown never finished (#4506).
  private allReadersFinishedWaiters: Array<() => void> = [];

  /** Returns false if shutdown has started (caller should skip the operation). */
  acquireRead(): boolean {
    if (this.shuttingDown) return false;
    this.readers++;
    return true;
  }

  releaseRead(): void {
    this.readers--;
    if (this.readers === 0 && this.allReadersFinishedWaiters.length > 0) {
      // take the list before calling anyone, so a waiter that starts another shutdown
      // from its own continuation cannot have its entry dropped by this same drain
      const waiters = this.allReadersFinishedWaiters;
      this.allReadersFinishedWaiters = [];
      for (const wake of waiters) {
        wake();
      }
    }
  }

  /** Blocks until all current readers finish, then permanently locks. */
  async acquireWrite(): Promise<void> {
    this.shuttingDown = true;
    if (this.readers > 0) {
      await new Promise<void>(resolve => {
        this.allReadersFinishedWaiters.push(resolve);
      });
    }
  }

  get isShuttingDown(): boolean {
    return this.shuttingDown;
  }

  /** How many read sections are open right now. Read-only, for reports: a shutdown stuck in
   *  `acquireWrite` is waiting for exactly this many to release (df-5c749cefc673). */
  get readerCount(): number {
    return this.readers;
  }
}
